import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { callAIStructured, AIGatewayError } from "./ai-gateway";
import { tryFetchOnetCareerGraph } from "./fetch-career-graph";
import { enforceQuota, incrementUsage, QuotaExceededError } from "./lib/quota";
import { CAPABILITIES } from "@/lib/quotas";

export type CareerCategory =
  | "current"
  | "Similar"
  | "Lateral"
  | "Advancement"
  | "Feeder";

export type CareerNode = {
  id: string;
  role: string;
  category: CareerCategory;
  requiredSkills: string[];
  salaryBand?: string;
  medianWage?: number | null;
  wageRange?: { low: number | null; high: number | null };
  brightOutlook?: boolean;
  brightOutlookCategories?: string[];
  rationale?: string;
  children?: CareerNode[];
};

const NODE_SCHEMA: Record<string, unknown> = {
  type: "object",
  properties: {
    id: { type: "string" },
    role: { type: "string" },
    category: {
      type: "string",
      enum: ["current", "Similar", "Lateral", "Advancement", "Feeder"],
    },
    requiredSkills: { type: "array", items: { type: "string" } },
    salaryBand: { type: "string" },
    rationale: { type: "string" },
  },
  required: ["id", "role", "category", "requiredSkills"],
  additionalProperties: false,
};

const CAREER_TOOL = {
  name: "save_career_tree",
  description:
    "Return a career path tree rooted at the candidate's current role, with three branches: Similar, Lateral, and Advancement. Each branch has 2-3 leaf roles.",
  parameters: {
    type: "object",
    properties: {
      rootRole: {
        type: "string",
        description: "The candidate's current or most recent role.",
      },
      similar: {
        type: "array",
        items: NODE_SCHEMA,
        description: "2-3 closely-related role suggestions (same level, similar work).",
      },
      lateral: {
        type: "array",
        items: NODE_SCHEMA,
        description: "2-3 lateral pivot suggestions (adjacent disciplines).",
      },
      advancement: {
        type: "array",
        items: {
          ...NODE_SCHEMA,
          properties: {
            ...(NODE_SCHEMA.properties as Record<string, unknown>),
            children: {
              type: "array",
              items: NODE_SCHEMA,
              description: "Optional next level beyond this advancement role.",
            },
          },
        },
        description: "2-3 promotion / level-up suggestions, each may have 1 child.",
      },
    },
    required: ["rootRole", "similar", "lateral", "advancement"],
    additionalProperties: false,
  },
};

type CareerToolResponse = {
  rootRole: string;
  similar: CareerNode[];
  lateral: CareerNode[];
  advancement: CareerNode[];
};

function buildTree(resp: CareerToolResponse): CareerNode {
  const tag = (nodes: CareerNode[], cat: CareerCategory): CareerNode[] =>
    nodes.map((n, i) => ({
      ...n,
      id: n.id || `${cat.toLowerCase()}-${i}`,
      category: cat,
      children: n.children ? tag(n.children, cat) : undefined,
    }));

  return {
    id: "root",
    role: resp.rootRole,
    category: "current",
    requiredSkills: [],
    children: [
      {
        id: "sim",
        role: "Similar Roles",
        category: "Similar",
        requiredSkills: [],
        children: tag(resp.similar, "Similar"),
      },
      {
        id: "lat",
        role: "Lateral Moves",
        category: "Lateral",
        requiredSkills: [],
        children: tag(resp.lateral, "Lateral"),
      },
      {
        id: "adv",
        role: "Advancement",
        category: "Advancement",
        requiredSkills: [],
        children: tag(resp.advancement, "Advancement"),
      },
    ],
  };
}

export const generateCareerPath = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { force?: boolean } | undefined) => input ?? {})
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // Load profile sources first so we can resolve the *current* expected role
    // and detect cache staleness.
    const { data: candidateRow } = await supabase
      .from("candidate_profiles")
      .select("verified_skills, chronological_experience, linkedin_data")
      .eq("user_id", userId)
      .maybeSingle();

    const { data: profileRow } = await supabase
      .from("profiles")
      .select("target_role, headline")
      .eq("id", userId)
      .maybeSingle();

    // Resolve a "current role" string. chronological_experience may be an
    // array OR an object like { experience: [...] } / { items: [...] }
    // depending on which ingestion path stored it.
    type ExpItem = { title?: string; role?: string; position?: string };
    const expRaw = candidateRow?.chronological_experience ?? null;
    let expArr: ExpItem[] = [];
    if (Array.isArray(expRaw)) {
      expArr = expRaw as ExpItem[];
    } else if (expRaw && typeof expRaw === "object") {
      const obj = expRaw as Record<string, unknown>;
      const candidate =
        (Array.isArray(obj.experience) && obj.experience) ||
        (Array.isArray(obj.items) && obj.items) ||
        (Array.isArray(obj.chronologicalExperience) && obj.chronologicalExperience) ||
        [];
      expArr = candidate as ExpItem[];
    }
    const firstExp = expArr.length > 0 ? expArr[0] : null;
    const ld = (candidateRow?.linkedin_data ?? {}) as { headline?: string };
    const candidateRole =
      profileRow?.target_role?.trim() ||
      firstExp?.title ||
      firstExp?.role ||
      firstExp?.position ||
      profileRow?.headline?.trim() ||
      ld.headline?.trim() ||
      "";

    // Return cached tree unless force=true OR cached role no longer matches
    // the freshly-resolved candidate role (auto-refresh on stale target).
    if (!data?.force) {
      const { data: cached } = await supabase
        .from("career_paths")
        .select("role_title, tree, updated_at")
        .eq("user_id", userId)
        .maybeSingle();
      const cachedRole = cached?.role_title?.toLowerCase().trim() ?? "";
      const wantedRole = candidateRole.toLowerCase().trim();
      const stale = wantedRole && cachedRole && !cachedRole.includes(wantedRole) && !wantedRole.includes(cachedRole);
      if (
        cached?.tree &&
        Object.keys(cached.tree as object).length > 0 &&
        !stale
      ) {
        return {
          tree: cached.tree as unknown as CareerNode,
          roleTitle: cached.role_title,
          cached: true,
        };
      }
    }

    // Quota check applies only when we actually generate (cache miss / forced).
    try {
      await enforceQuota(supabase, userId, CAPABILITIES.career_path);
    } catch (e) {
      if (e instanceof QuotaExceededError) throw new Response(e.message, { status: e.status });
      throw e;
    }

    // 1) Try O*NET first (real labour-market data)
    if (candidateRole) {
      const onet = await tryFetchOnetCareerGraph(candidateRole, supabase);
      if (onet) {
        await supabase.from("career_paths").upsert(
          {
            user_id: userId,
            role_title: onet.rootTitle,
            tree: onet.tree as unknown as never,
          },
          { onConflict: "user_id" },
        );
        await incrementUsage(userId, CAPABILITIES.career_path);
        return { tree: onet.tree, roleTitle: onet.rootTitle, cached: false };
      }
    }

    // 2) Fallback: LLM-generated tree
    const profileSummary = JSON.stringify(
      {
        skills: candidateRow?.verified_skills ?? [],
        experience: candidateRow?.chronological_experience ?? {},
        linkedin: candidateRow?.linkedin_data ?? {},
      },
      null,
      2,
    ).slice(0, 12_000);

    let resp: CareerToolResponse;
    try {
      resp = await callAIStructured<CareerToolResponse>({
        messages: [
          {
            role: "system",
            content:
              "You are a career strategist. Given a candidate profile, return a personalised career path tree. Suggest 2-3 SIMILAR roles (same seniority, related work), 2-3 LATERAL pivots (adjacent disciplines using their existing skills), and 2-3 ADVANCEMENT roles (next level up — some with 1 further step beyond). For each leaf, list 4-6 concrete required skills, a realistic salary band like '$140k - $180k', and a 1-sentence rationale tying it to the candidate's profile.",
          },
          {
            role: "user",
            content: `Candidate Profile:\n${profileSummary}`,
          },
        ],
        tool: CAREER_TOOL,
      });
    } catch (e) {
      if (e instanceof AIGatewayError)
        throw new Response(e.message, { status: e.status });
      throw e;
    }

    const tree = buildTree(resp);

    await supabase.from("career_paths").upsert(
      {
        user_id: userId,
        role_title: resp.rootRole,
        tree: tree as unknown as never,
      },
      { onConflict: "user_id" },
    );

    await incrementUsage(userId, CAPABILITIES.career_path);

    return { tree, roleTitle: resp.rootRole, cached: false };
  });
